import { takeLatest, takeEvery, put, call } from 'redux-saga/effects';
import { SET_TREE, SET_LISTS, FETCH_TREE, CREATE_LIST, FETCH_LISTS,
	DELETE_LIST, UPDATE_LIST, ADD_REMOVE_ITEM, SET_CURRENT_LIST, TREE_IS_LOADED,
	SET_BANNER, SET_ACCOUNTS } from '../actions/constants';
import { cleanListEntities, buildAccounts } from '../helpers/utility.js';
import { MESSAGE_TYPE_ERROR } from '../global-constants.js';
import { t } from 'i18next';
import soaTestData from '../__tests__/data/soa-test.json';

const fetchJSON = async (url, method, headers, body) => {
	const response = await fetch(url, {
		method: method,
		headers: headers,
		body: JSON.stringify(body),
	});

	if (response.status >= 400) {
		throw new Error(response.statusText);
	} else {
		// handles 200 with no response
		const data = await response.text().then(text => {
			return text ? JSON.parse(text) : {};
		});
		return data;
	}
}
// get this from the DOM because Control Center may change it
const SERVICE_URL = document.getElementById('dist-list-ui-app-root').dataset.serviceUrl;

function* onFetchTree() {
	yield put({ type: SET_BANNER, payload: { message: '', type: '', autoDismiss: false }});
	try {
		yield put({ type: TREE_IS_LOADED, payload: false });

		let result = {};
		if (process.env.NODE_ENV !== 'test') {
			result = yield call(fetchJSON,
				`${SERVICE_URL}/accountHierarchy/${ddc.global.account.accountId}`,
				'GET',
				{
					Accept: 'application/vnd.dealer.distributionlist.v1+json',
				}
			);
		} else {
			// Load test data (dev mode only)
			result = soaTestData;
			// result = gmTestData; // uncomment to test GM account
		}

		yield put({ type: SET_TREE, payload: result });
		yield put({ type: TREE_IS_LOADED, payload: true });

		if (result.root) {
			// Build list of unique accounts
			const accounts = yield call(buildAccounts, result.root);
			yield put({ type: SET_ACCOUNTS, payload: accounts });
		} else {
			throw new Error();
		}
	}
	catch(error) {
		yield put({ type: SET_BANNER, payload: { message: t('ERROR_UNABLE_TO_LOAD_DATA'), type: MESSAGE_TYPE_ERROR }});
	}
}

function* onCreateList(action) {
	yield put({ type: SET_BANNER, payload: { message: '', type: '', autoDismiss: false }});
	try {
		const result = yield call(fetchJSON,
			`${SERVICE_URL}/`,
			'POST',
			{
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			{
				'accountId': ddc.global.account.accountId,
				'createdBy': ddc.global.actualUser.userId,
				'distList': action.payload.list,
				'name': action.payload.name,
				'lastUpdatedBy': ddc.global.actualUser.userId,
			}
		);

		yield put({ type: FETCH_LISTS, payload: result });

		// always select the latest-created list
		yield put({ type: SET_CURRENT_LIST, payload: {
			distList: result.distList,
			id: result.id,
			isFirstLoad: true,
			name: result.name,
		}});
	}
	catch(error) {
		yield put({ type: SET_BANNER, payload: { message: t('ERROR_UNABLE_TO_LOAD_LISTS'), type: MESSAGE_TYPE_ERROR }});
	}
}

function* onDeleteList(action) {
	yield put({ type: SET_BANNER, payload: { message: '', type: '', autoDismiss: false }});
	try {
		const result = yield call(fetchJSON,
			`${SERVICE_URL}/id/${action.payload.id}`,
			'DELETE',
		);

		yield put({ type: FETCH_LISTS, payload: result });
	}
	catch(error) {
		yield put({ type: SET_BANNER, payload: { message: t('ERROR_UNABLE_TO_SAVE_CHANGES'), type: MESSAGE_TYPE_ERROR }});
	}
}

function* onUpdateList(action) {
	try {
		const result = yield call(fetchJSON,
			`${SERVICE_URL}/id/${action.id}`,
			'PUT', // this behaves like PATCH on the back end
			{
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
			// so you can just send in the fields to update here
			action.payload
		);

		yield put({ type: FETCH_LISTS, payload: result });
		// need to reset current list when name changes
		yield put({ type: SET_CURRENT_LIST, payload: {
			distList: result.distList,
			id: result.id,
			isFirstLoad: false,
			name: result.name,
		}});
	}
	catch(error) {
		yield put({ type: SET_BANNER, payload: { message: t('ERROR_UNABLE_TO_SAVE_CHANGES'), type: MESSAGE_TYPE_ERROR }});
	}
}

function* onFetchLists(action) {
	try {
		const result = yield call(fetchJSON,
			`${SERVICE_URL}/accountId/${ddc.global.account.accountId}`,
			'GET',
			{
				Accept: 'application/vnd.dealer.distributionlist.v1+json',
				'If-None-Match': "01b74a2c15835811d87dd1904d643ede1" //eslint-disable-line
			}
		);

		// Backend is escaping HTML entities in 'name', so decode this field
		const cleanedResult = yield call(cleanListEntities, result);
		yield put({ type: SET_LISTS, payload: cleanedResult });
	}
	catch(error) {
		yield put({ type: SET_BANNER, payload: { message: t('ERROR_UNABLE_TO_LOAD_LISTS'), type: MESSAGE_TYPE_ERROR }});
	}
}

export default function* listenForRequests() {
	yield takeLatest(FETCH_TREE, onFetchTree);
	yield takeLatest(FETCH_LISTS, onFetchLists);
	yield takeEvery(CREATE_LIST, onCreateList);
	yield takeEvery(DELETE_LIST, onDeleteList);
	yield takeEvery(UPDATE_LIST, onUpdateList);
	yield takeEvery(ADD_REMOVE_ITEM, onUpdateList);
}